/**
 * Señales de un posible intento de captura de pantalla (ADR-0029).
 *
 * El navegador no deja ver una captura: lo que sí ve es el teclado. Dos
 * señales:
 *
 * - **Impr Pant** (Windows, Linux): llega como `PrintScreen`.
 * - **La combinación del sistema**: ⌘⇧3/4/5 en Mac, ⊞⇧S en Windows. Si el
 *   navegador recibe la tecla final, se reconoce de una vez; casi siempre el
 *   sistema se la queda, y lo que llega es ⌘/⊞ y ⇧ juntas que se sueltan **sin
 *   ninguna otra tecla**. Ningún atajo del navegador hace eso: ⌘⇧T trae su T.
 *
 * No hay señal para un celular, para una herramienta de recorte con el ratón
 * ni para una foto a la pantalla. Para eso está la marca de agua con el nombre.
 */
export type SenalDeCaptura = 'print_screen' | 'shortcut';

/** Lo que el detector necesita de un evento del navegador. */
export type EventoParaCapturas =
  | { tipo: 'keydown' | 'keyup'; key: string; metaKey: boolean; shiftKey: boolean }
  /** Mover el ratón trae el estado de las teclas: si se soltaron sin que llegara su `keyup`. */
  | { tipo: 'puntero'; metaKey: boolean; shiftKey: boolean }
  /** La ventana perdió el foco: la herramienta de captura del sistema se puso enfrente. */
  | { tipo: 'blur' };

/** La tecla final de las combinaciones de captura, cuando sí llega. */
const TECLAS_FINALES = new Set(['3', '4', '5', '6', 's', 'S']);
const MODIFICADORES = new Set(['Meta', 'Shift', 'Control', 'Alt', 'OS']);

/**
 * Un detector con memoria: recibe los eventos en orden y contesta, en el que
 * delata una captura, con su señal.
 */
export function crearDetectorDeCapturas(): (evento: EventoParaCapturas) => SenalDeCaptura | null {
  /** ⌘/⊞ y ⇧ abajo, y todavía sin otra tecla. */
  let armado = false;
  let otraTecla = false;

  const soltar = (): SenalDeCaptura | null => {
    const era = armado && !otraTecla;
    armado = false;
    otraTecla = false;
    return era ? 'shortcut' : null;
  };

  return (evento) => {
    if (evento.tipo === 'blur') return soltar();
    if (evento.tipo === 'puntero') return armado && !(evento.metaKey && evento.shiftKey) ? soltar() : null;

    if (evento.key === 'PrintScreen') {
      // Chrome en Windows sólo manda el `keyup`; en Linux llegan los dos. Con uno basta.
      return evento.tipo === 'keyup' ? 'print_screen' : null;
    }

    if (evento.tipo === 'keydown') {
      if (MODIFICADORES.has(evento.key)) {
        if (evento.metaKey && evento.shiftKey && !armado) {
          armado = true;
          otraTecla = false;
        }
        return null;
      }
      if (evento.metaKey && evento.shiftKey) {
        if (TECLAS_FINALES.has(evento.key)) {
          armado = false;
          return 'shortcut';
        }
        // ⌘⇧T, ⌘⇧[ …: un atajo del navegador, no una captura.
        otraTecla = true;
      }
      return null;
    }

    if (evento.key === 'Meta' || evento.key === 'OS' || evento.key === 'Shift') return soltar();
    return null;
  };
}
